import { forwardRef, useEffect, useId, useLayoutEffect, useRef, useState, type TextareaHTMLAttributes } from "react";
import { useWorkspaceCopy } from "../i18n/workspaceCatalog";
import { SafeRichTextEditor } from "./RichTextEditor";

/** Native textarea/FormData contract with the canonical safe formatting toolbar. */
export const RichTextArea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function RichTextArea({ value, defaultValue, onChange, rows = 5, maxLength, name, ...props }, forwardedRef) {
  const { locale } = useWorkspaceCopy();
  const id = useId();
  const field = useRef<HTMLTextAreaElement>(null);
  const [draft, setDraft] = useState(String(defaultValue ?? ""));
  const [label, setLabel] = useState<string>();
  const dirty = useRef(false);
  const current = value === undefined ? draft : String(value);

  useLayoutEffect(() => {
    const element = field.current;
    if (!element) return;
    const owner = element.closest("label");
    const previousFor = owner?.getAttribute("for");
    if (owner && !previousFor) owner.htmlFor = element.id;
    const associated = owner ?? element.labels?.[0];
    setLabel(associated ? Array.from(associated.childNodes).filter(node => node.nodeType === Node.TEXT_NODE).map(node => node.textContent).join(" ").trim() || undefined : undefined);
    return () => { if (owner && !previousFor) owner.removeAttribute("for"); };
  }, [props.id, locale]);

  useEffect(() => {
    const form = field.current?.form;
    const reset = () => { dirty.current = false; setDraft(String(defaultValue ?? "")); };
    form?.addEventListener("reset", reset);
    if (!dirty.current && value === undefined) setDraft(String(defaultValue ?? ""));
    return () => form?.removeEventListener("reset", reset);
  }, [defaultValue, value]);

  function change(next: string) {
    dirty.current = true;
    if (value === undefined) setDraft(next);
    const element = field.current;
    if (!element || element.value === next) return;
    // Bypass React's value tracker so toolbar edits emit the same native input event as typing.
    Object.getOwnPropertyDescriptor(HTMLTextAreaElement.prototype, "value")?.set?.call(element, next);
    element.dispatchEvent(new Event("input", { bubbles: true }));
  }

  return <SafeRichTextEditor name={name ?? ""} value={current} onChange={change} rows={rows} maxLength={maxLength ?? null}
    placeholder={props.placeholder} required={props.required} ariaLabel={props["aria-label"] ?? label}
    textareaProps={{ ...props, id: props.id ?? `rich-text-${id}`, onChange }}
    textareaRef={(element) => { field.current = element; if (typeof forwardedRef === "function") forwardedRef(element); else if (forwardedRef) forwardedRef.current = element; }} />;
});
